import { useLayoutEffect, useRef, useState } from "react";
import { CARD_RATIO } from "@/lib/artAssets";

// Card-detail slot sized so the slot region (card + whatever sits under it: card buttons, tile-card
// tabs) ends level with the bottom of the board. Measured live from the DOM, so it follows the board
// size, the players list above the slot and the panel under the card in the wide and compact layout.
//   width  : card width, up to max (the column width); normally at least min (the old fixed slot
//            width), smaller only when a tall panel under the card (e.g. demolish confirm) needs room
//            so its buttons are never cut off (never below FLOOR)
//   regionH: slot top -> board bottom (the region's min height; the buttons sit at its bottom)
//   emptyH : height the empty "Select a card" box may fill (down to the buttons / board bottom)
const GAP = 6; // slot-region flex gap (GameScreen)
const BUTTON_RESERVE = 50; // one row of card buttons (34) + one reason line (3 + 13)
const FLOOR = 150; // smallest card width ever drawn
const WATCH = ['[data-testid="board-frame"]', '[data-testid="selected-card-area"]', '[data-testid="slot-region"]', '[data-testid="slot-caption"]', '[data-testid="slot-buttons"]'];

// Returns [{ width, regionH, emptyH }, regionRef]; put regionRef on the slot region (inside the main area).
export default function useSlotFit({ min, max }) {
  const [fit, setFit] = useState({ width: min, regionH: null, emptyH: null });
  const regionRef = useRef(null);
  useLayoutEffect(() => {
    const root = regionRef.current?.closest('[data-testid="main-area"]');
    if (!root || typeof ResizeObserver === "undefined") return undefined;
    let raf = 0;
    const measure = () => {
      raf = 0;
      const frame = root.querySelector('[data-testid="board-frame"]');
      const slot = root.querySelector('[data-testid="card-detail-slot"]');
      if (!frame || !slot) return;
      // Stage scale (the stage is CSS-scaled; offset sizes are stage px, rects are screen px).
      const k = root.offsetHeight ? root.getBoundingClientRect().height / root.offsetHeight : 1;
      // Rows under the card (hidden ones take no space: an empty button row is display:none).
      const below = [...root.querySelectorAll('[data-testid="slot-caption"], [data-testid="slot-buttons"]')].filter((el) => el.getClientRects().length > 0);
      const belowH = below.reduce((sum, el) => sum + el.getBoundingClientRect().height / k + GAP, 0);
      const regionH = Math.floor((frame.getBoundingClientRect().bottom - slot.getBoundingClientRect().top) / k);
      const fitW = Math.floor((regionH - Math.max(belowH, BUTTON_RESERVE + GAP)) / CARD_RATIO);
      // Only the standard button row under the card: never smaller than before (min). A taller panel
      // shrinks the card so the panel fits above the board bottom.
      const tall = belowH > BUTTON_RESERVE + 2 * GAP + 13;
      const width = Math.max(tall ? FLOOR : min, Math.min(max, fitW));
      const emptyH = Math.floor(regionH - belowH);
      setFit((prev) => (prev.width === width && prev.regionH === regionH && prev.emptyH === emptyH ? prev : { width, regionH, emptyH }));
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };
    const ro = new ResizeObserver(schedule);
    const watch = () => {
      ro.disconnect();
      ro.observe(root);
      root.querySelectorAll(WATCH.join(",")).forEach((el) => ro.observe(el));
      schedule();
    };
    // Panels under the card mount / unmount (tile tabs, demolish, action cards): re-attach and re-measure.
    const mo = new MutationObserver(watch);
    mo.observe(regionRef.current.closest('[data-testid="selected-card-area"]') || root, { childList: true, subtree: true });
    watch();
    measure(); // first size before paint (no flash at the minimum width)
    return () => {
      mo.disconnect();
      ro.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [min, max]);
  return [{ ...fit, width: Math.max(FLOOR, Math.min(max, fit.width)) }, regionRef];
}
